import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsLeftRight, ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const ease = [0.22, 1, 0.36, 1];

const PAIRS = [
  {
    id: "echoppe",
    avant: "/images/avant-chantier.jpg",
    apres: "/images/apres-toiture.jpg",
    altAvant: "Chantier en cours : écran sous-toiture et liteaux apparents",
    altApres: "Toiture finie en tuiles terre cuite avec fenêtre de toit",
    titre: "Échoppe bordelaise",
    detail: "Dépose totale, écran HPV, isolation, tuiles terre cuite et Vélux neuf.",
  },
  {
    id: "canal",
    avant: "/images/avant-voliges.jpg",
    apres: "/images/apres-canal.jpg",
    altAvant: "Chantier en cours : voliges et liteaux neufs",
    altApres: "Toiture finie en tuiles canal",
    titre: "Tuile canal du Sud-Ouest",
    detail: "Voligeage et liteaux neufs, tuiles canal, faîtage scellé.",
  },
];

export default function BeforeAfter() {
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const pair = PAIRS[idx];

  const updateFromClientX = (clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const x = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : null);
      if (x != null) updateFromClientX(x);
    };
    const up = () => setDragging(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", up);
    };
  }, [dragging]);

  const startDrag = (clientX) => {
    setDragging(true);
    updateFromClientX(clientX);
  };
  const switchPair = (next) => {
    setIdx((next + PAIRS.length) % PAIRS.length);
    setPos(50);
  };

  return (
    <section data-testid="beforeafter-section" className="relative border-t border-white/5 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="Avant / Après"
          title="Le même toit, transformé"
          copy="Glissez le curseur : à gauche le chantier en cours, à droite la couverture finie. Plusieurs chantiers à faire défiler."
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="mt-10"
        >
          <div
            ref={trackRef}
            data-testid="beforeafter-slider"
            onPointerDown={(e) => startDrag(e.clientX)}
            onTouchStart={(e) => startDrag(e.touches[0].clientX)}
            onDragStart={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            style={{ touchAction: "none", WebkitTouchCallout: "none" }}
            className="relative aspect-[4/5] w-full cursor-ew-resize touch-none select-none overflow-hidden border border-white/10 sm:aspect-[16/10]"
          >
            <AnimatePresence mode="sync">
              <motion.div
                key={pair.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease }}
                className="absolute inset-0"
              >
                <img
                  src={pair.apres}
                  alt={pair.altApres}
                  draggable={false}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
                <img
                  src={pair.avant}
                  alt={pair.altAvant}
                  draggable={false}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                  style={{
                    clipPath: `inset(0 ${100 - pos}% 0 0)`,
                    transition: dragging ? "none" : "clip-path 0.5s cubic-bezier(0.22,1,0.36,1)",
                  }}
                />
              </motion.div>
            </AnimatePresence>

            <span
              data-testid="beforeafter-label-avant"
              className="absolute left-4 top-4 border border-white/30 bg-black/85 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur"
              style={{ opacity: pos > 12 ? 1 : 0, transition: "opacity 0.3s" }}
            >
              Avant · chantier
            </span>
            <span
              data-testid="beforeafter-label-apres"
              className="absolute right-4 top-4 border border-terra/70 bg-black/85 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-terra backdrop-blur"
              style={{ opacity: pos < 88 ? 1 : 0, transition: "opacity 0.3s" }}
            >
              Après · fini
            </span>

            <div
              data-testid="beforeafter-handle"
              className="absolute inset-y-0 z-10"
              style={{
                left: `${pos}%`,
                transition: dragging ? "none" : "left 0.5s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <div className="absolute inset-y-0 -left-px w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.6)]" />
              <div className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-black/60 text-white backdrop-blur-md">
                <ChevronsLeftRight className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p data-testid="beforeafter-pair-title" className="font-serif text-base text-slate-100">{pair.titre}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{pair.detail}</p>
            </div>
            <div className="flex rounded-full border border-white/15 bg-black/40 p-1">
              <button
                data-testid="beforeafter-btn-avant"
                onClick={() => setPos(82)}
                className={`rounded-full px-4 py-1.5 text-[11px] font-semibold transition-colors duration-300 ${
                  pos > 60 ? "bg-terra text-white" : "text-slate-300 hover:text-white"
                }`}
              >
                Voir l'avant
              </button>
              <button
                data-testid="beforeafter-btn-apres"
                onClick={() => setPos(18)}
                className={`rounded-full px-4 py-1.5 text-[11px] font-semibold transition-colors duration-300 ${
                  pos <= 60 ? "bg-terra text-white" : "text-slate-300 hover:text-white"
                }`}
              >
                Voir l'après
              </button>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-center gap-5 border-t border-white/5 pt-5">
            <button
              data-testid="beforeafter-prev"
              onClick={() => switchPair(idx - 1)}
              aria-label="Chantier précédent"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors duration-300 hover:border-terra hover:text-terra"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2.5">
              {PAIRS.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`beforeafter-dot-${p.id}`}
                  onClick={() => switchPair(i)}
                  aria-label={`Voir le chantier ${p.titre}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? "w-7 bg-terra" : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
            <button
              data-testid="beforeafter-next"
              onClick={() => switchPair(idx + 1)}
              aria-label="Chantier suivant"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors duration-300 hover:border-terra hover:text-terra"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
